import React from 'react'
import {
  LBButton,
  LBDropdownMenuCheckboxItem,
  LBDropdownMenuItem,
  LBDropdownMenuLabel,
  LBTooltip,
  LBTooltipContent,
  LBTooltipTrigger,
  LBXIcon
} from '@linearb/buzz'
import _ from 'lodash'
import {
  getAllRoleScopeDefinitions,
  getAllScopeDefinitions
} from '../constants/scopes'
import useUserInfo from '../hooks/useUserInfo'
import { scopePreviewStorage } from '../utils/scopePreviewStorage'

// null resets to the real scopes; [] is a real preview with no scopes at all
const applyPreview = (scopes: string[] | null) =>
  scopes === null
    ? scopePreviewStorage.clear()
    : scopePreviewStorage.set(scopes)

/**
 * "View as" — renders the app as if you held a different scope set, so hidden
 * and disabled controls can be seen from another role's side.
 *
 * Purely visual: the backend still authorizes from the real JWT, so a control
 * revealed by adding a scope you lack will simply be refused.
 */
export const ScopePreviewMenu: React.FC = () => {
  const userInfo = useUserInfo()

  if (!userInfo) {
    return null
  }

  const { scopes, realScopes, isPreviewing } = userInfo

  const toggle = (name: string, checked: boolean) =>
    applyPreview(checked ? [...scopes, name] : _.without(scopes, name))

  // The gate vocabulary, not a capability list: both maps share
  // `backoffice_scopes` and both gate the UI
  const rows = [...getAllScopeDefinitions(), ...getAllRoleScopeDefinitions()]

  return (
    <>
      <LBDropdownMenuLabel className='font-normal'>
        <p className='text-xs font-medium text-muted-foreground'>View as</p>
      </LBDropdownMenuLabel>
      {rows.map(({ name, label }) => (
        <LBDropdownMenuCheckboxItem
          key={name}
          checked={scopes.includes(name)}
          onCheckedChange={checked => toggle(name, checked)}
          // Dimmed when it is not a scope you actually hold
          className={realScopes.includes(name) ? '' : 'opacity-50'}
        >
          <span className='font-mono text-xs'>{name}</span>
          <span className='text-xs text-muted-foreground'>{label}</span>
        </LBDropdownMenuCheckboxItem>
      ))}
      {isPreviewing && (
        <LBDropdownMenuItem
          onClick={() => applyPreview(null)}
          className='cursor-pointer'
        >
          Reset to my real scopes
        </LBDropdownMenuItem>
      )}
    </>
  )
}

/**
 * Always-visible reminder that the page is not being rendered with your own
 * permissions, with a one-click exit.
 */
export const ScopePreviewBadge: React.FC = () => {
  const userInfo = useUserInfo()

  if (!userInfo?.isPreviewing) {
    return null
  }

  const { scopes } = userInfo

  return (
    <LBTooltip>
      <LBTooltipTrigger asChild>
        <LBButton
          variant='destructive'
          size='sm'
          className='h-8 gap-1 whitespace-nowrap'
          onClick={() => applyPreview(null)}
        >
          Viewing as: {scopes.length > 0 ? scopes.join(', ') : 'no scopes'}
          <LBXIcon className='h-3 w-3' />
        </LBButton>
      </LBTooltipTrigger>
      <LBTooltipContent className='max-w-64'>
        Display only - the backend still enforces your real permissions
      </LBTooltipContent>
    </LBTooltip>
  )
}
